import { useCallback } from 'react';
import { isEmpty } from 'lodash-es';
import { faAnglesLeft, faCircle, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { useFormValues, useSetFieldValue } from '@s20/components/forms/FormContext';
import { Block } from '../../../common/Block';
import { Flex, StyledButton } from '../../../common/forms/FormElements';
import { DraggableResourceTypes } from '../../../dispatch/Dispatch';
import { EmployeeDispatchFilters } from '../common/constants';
import { UncontrolledSaveButton } from '../../../common/forms/SaveButton';
import { useAssignResourcesButton } from './useAssignResourcesButton.hook';

export const Toolbar = ({ selected, setSelected, totalAvailable }) => {
    const {
        filters, selectedResourceType, selectedResourceGroup, selectedCertifications,
        replacementAssignmentId,
    } = useFormValues();
    const setFieldValue = useSetFieldValue();

    const { handleAssignmentButtonClick, packageAssignmentData, resetReplacementState } = useAssignResourcesButton({ setSelected, totalAvailable });

    const handleFilterClick = useCallback((filterValue) => {
        let currentFilters = [...(filters || [])];

        if (currentFilters.includes(filterValue)) {
            currentFilters = currentFilters.filter((val) => val !== filterValue);
        } else {
            currentFilters.push(filterValue);
        }

        setFieldValue('filters', currentFilters);
    }, [filters, setFieldValue]);

    return (
        <>
            <Block
                color="dark"
                className="toolbar pl-3 pr-3 flex-none"
            >
                <Flex alignItems="center">
                    <UncontrolledSaveButton
                        color="light"
                        className="mr-4"
                        disabled={isEmpty(selected)}
                        onClick={() => handleAssignmentButtonClick.mutateAsync(packageAssignmentData(selected))}
                        icon={faAnglesLeft}
                        label={`Assign ${selectedResourceType === DraggableResourceTypes.Staff ? 'Staff' : 'Equipment'}`}
                        savingLabel="Assigning..."
                        type="button"
                    />

                    <div>
                        {selected.length || 0} of {totalAvailable} selected
                    </div>
                </Flex>
            </Block>

            <Flex
                justifyContent="end"
                fill={false}
                className="mb-3"
            >
                {selectedResourceType === DraggableResourceTypes.Staff && Object.entries(EmployeeDispatchFilters).map(([key, label]) => {
                    const filterSelected = (filters || []).includes(key);

                    let icon = faCircle;
                    if (filterSelected) {
                        icon = faCircleCheck;
                    }

                    return (
                        <StyledButton
                            key={key}
                            size="sm"
                            startIcon={icon}
                            outlined
                            color="light"
                            onClick={() => handleFilterClick(key)}
                        >
                            {label}
                        </StyledButton>
                    );
                })}

                {Boolean(selectedResourceGroup) && (
                    <StyledButton
                        size="sm"
                        startIcon={faCircleCheck}
                        outlined
                        color="light"
                        onClick={() => {
                            setFieldValue('selectedResourceGroup', null);

                            // Abandoning the replace flow before an assignment is picked —
                            // clear all replace-mode state so a later unrelated Assign
                            // doesn't get silently redirected to replaceAssignment using
                            // this stale assignmentId.
                            if (replacementAssignmentId) {
                                resetReplacementState();
                            }
                        }}
                    >
                        {selectedResourceGroup.description || selectedResourceGroup.name}
                    </StyledButton>
                )}

                {Boolean(selectedCertifications) && (
                    <StyledButton
                        size="sm"
                        startIcon={faCircleCheck}
                        outlined
                        color="light"
                        onClick={() => setFieldValue('selectedCertifications', null)}
                    >
                        Requires Certifications
                    </StyledButton>
                )}
            </Flex>
        </>
    );
};